import { ROUTE_PATHS } from "@/constants";

// Pure `next` -> in-app path mapper, shared by CallbackPage and LoginPage.
// Kept in its own dependency-light module (only ROUTE_PATHS) so the login
// chunk doesn't pull in the lazy CallbackPage chunk and its transitive deps
// (AuthStalledScreen, LiffContext, firebase/auth) just for this mapping.
export const getCallbackDestination = (
  searchParams: URLSearchParams,
): string => {
  const nextPath = searchParams.get("next")?.trim();

  if (!nextPath) {
    return ROUTE_PATHS.MEMBER.INFORMATION;
  }

  // `next` is a route segment ("trainers", "feedback", "member-credit"), never
  // an absolute or protocol-relative URL. Reject anything that could escape to
  // another origin/scheme — a leading slash/backslash, an embedded "//", or a
  // scheme would let ?next=/evil.com (→ "//evil.com") or ?next=https:evil.com
  // redirect off-site once this value is navigated to. Fall back to the hub.
  const isUnsafe =
    /^[\\/]/.test(nextPath) ||
    nextPath.includes("//") ||
    nextPath.includes("\\") ||
    /^[a-z][a-z0-9+.-]*:/i.test(nextPath);
  if (isUnsafe) {
    return ROUTE_PATHS.MEMBER.INFORMATION;
  }

  // Feedback used to be a `?tab=` on the profile page; the hub redesign gave
  // it its own route, so the deep link points there directly.
  if (nextPath === "feedback") {
    return ROUTE_PATHS.MEMBER.FEEDBACK;
  }

  // Walk-in guest flow (QTY-1991): the LINE rich menu deep-links with
  // ?next=facilities-walk-in (dashed — a slash would read as a path segment
  // in the LIFF entry URL).
  if (nextPath === "facilities-walk-in") {
    return ROUTE_PATHS.WALK_IN.LIST;
  }

  return `/${nextPath}${nextPath === "trainers" ? "/pt" : ""}`;
};

// Destinations reachable without a member session. CallbackPage must navigate
// to these directly — routing a walk-in guest through the member /login would
// dead-end them (guests have no account).
export const isPublicCallbackDestination = (destination: string): boolean =>
  destination === ROUTE_PATHS.WALK_IN.LIST ||
  destination.startsWith(`${ROUTE_PATHS.WALK_IN.LIST}/`);

// On cold LIFF entry (https://liff.line.me/<liffId>?next=...&tenantId=...) the
// original query rides inside ?liff.state= until liff.init restores the URL —
// which may never reach the router (the SDK uses history.replaceState). If the
// outer params carry no `next`, look inside the envelope.
export const unwrapLiffState = (
  searchParams: URLSearchParams,
): URLSearchParams => {
  if (searchParams.get("next")) return searchParams;
  const liffState = searchParams.get("liff.state");
  if (!liffState) return searchParams;
  try {
    // liff.state carries path + query ("?next=..." or "/?next=..." depending
    // on entry-URL form) — strip everything up to the first "?" so the path
    // part never corrupts the first key.
    const queryStart = liffState.indexOf("?");
    return new URLSearchParams(
      queryStart >= 0 ? liffState.slice(queryStart + 1) : liffState,
    );
  } catch {
    return searchParams;
  }
};
